// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, TextareaAutosize } from '@mui/material';
// Components, Layouts, Pages
// Others
import { KeyboardEnum, MessageTypeEnum, ToastTypeEnum } from '~/utils/enum/common';
import { customToast } from '~/utils/helper/common';
import {
  ACCEPT_UPLOAD_DOCUMENT_MESSAGE,
  DEFAULT_HEIGHT_INPUT_MESSAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  MAX_SIZE_FILE_UPLOAD,
} from '~/utils/constants/common';
import { ISubmitMessage } from '~/utils/interface/message';
// Styles, images, icons
import styles from './ConversationInputMessage.module.scss';
import { icons } from '~/assets';

type Props = {
  onSubmit: (data: ISubmitMessage) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const cx = classNames.bind(styles);

const ConversationInputMessage = (props: Props) => {
  //#region Destructuring Props
  const { onSubmit, onFocus, onBlur } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/conversation']);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [inputValue, setInputValue] = useState<string>(EMPTY_STRING);
  const [inputHeight, setInputHeight] = useState<number | null>(null);
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleFocus = () => {
    onFocus && onFocus();
  };

  const handleBlur = () => {
    onBlur && onBlur();
  };

  const handleSubmit = () => {
    if (!inputValue.trim()) {
      return;
    }

    const data = {
      content: inputValue.trim(),
      type: MessageTypeEnum.TEXT,
    };

    onSubmit(data);
    setInputValue(EMPTY_STRING);
    setInputHeight(null);
  };

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (event.target.value) {
      setInputHeight(event.target.scrollHeight);
    } else {
      setInputHeight(null);
    }

    setInputValue(event.target.value);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.shiftKey && event.key === KeyboardEnum.ENTER) {
      return;
    }

    if (event.key === KeyboardEnum.ENTER) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleUploadFile = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const newFiles: File[] = [];

    Array.from(files).forEach((file) => {
      if (file.size <= MAX_SIZE_FILE_UPLOAD) {
        newFiles.push(file);
      } else {
        customToast(ToastTypeEnum.ERROR, t('message_max_file_size_upload_content', { ns: 'common/base' }));
      }
    });

    if (newFiles.length <= DEFAULT_NUMBER_ZERO) return;

    const dataUpload: ISubmitMessage = {
      type: MessageTypeEnum.FILE,
      files: newFiles,
    };

    onSubmit(dataUpload);

    if (fileInputRef.current) {
      fileInputRef.current.value = EMPTY_STRING;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='conversationInputMessage' className={cx('container')}>
      <Box component={'div'} className={cx('conversationContentGroup')}>
        <Box className={cx('inputMessageAction')}>
          <input
            ref={fileInputRef}
            type='file'
            id={'uploadInput'}
            className={cx('uploadInput')}
            multiple={false}
            accept={ACCEPT_UPLOAD_DOCUMENT_MESSAGE}
            onChange={handleUploadFile}
          />

          <IconButton component={'label'} htmlFor='uploadInput' className={cx('inputMessageActionItemAdd')}>
            <img
              src={icons.conversationIconUploadFile}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('inputMessageActionItem')}
            />
          </IconButton>
        </Box>

        <Box component={'div'} className={cx('inputMessageContent')}>
          <TextareaAutosize
            id='inputMessageInput'
            className={cx('inputMessageInput')}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleInputChange}
            value={inputValue}
            style={{ height: inputHeight ?? DEFAULT_HEIGHT_INPUT_MESSAGE }}
            maxRows={2}
            aria-label='maximum height'
            onKeyDown={handleInputKeyDown}
            placeholder={t('conversation_input_placeholder')}
          />
        </Box>
      </Box>

      <Box component={'div'} className={cx('btnSubmit')}>
        <Button onClick={handleSubmit} variant='contained' className={cx('inputMessageSend')}>
          {t('common_action_send', { ns: 'common/base' })}
        </Button>
      </Box>
    </Box>
  );
};

export default ConversationInputMessage;
